"use client";

import {useEffect, useState} from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Textarea,
  useToast,
} from "@mantis-sas/ui";
import Select from "react-select";
import {Loader2} from "lucide-react";
import {useNavigate} from "react-router-dom";

// Importar el componente FileUploadField
import FileUploadField from "./file-upload-field";
import {submitPublicForm} from "../../../ApiCalls/customForms";
import InventoryModal from "../Inventory/components/InventoryModal";
import {ClientFormModal} from "../Clients/Modals/ClientFormModal";
import {ProductFormModal} from "../Products/Modals/ProductFormModal";
import {getSubitemsByItemId} from "../../../ApiCalls/mantisFlowsItems";
import SubitemsModal from "./SubitemsModal";
import ProductCard from "./ProductCard";
import { connect } from "react-redux";
import { selectAuthState } from "../../../store/selectors/authSelectors";

// Add this helper function at the top of the file, after the imports
const addBusinessDays = (date, days) => {
  const result = new Date(date);
  let remainingDays = days;

  while (remainingDays > 0) {
    result.setDate(result.getDate() + 1);
    // Check if the day is a weekend (0 = Sunday, 6 = Saturday)
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      remainingDays--;
    }
  }

  return result;
};

// Add this helper function after the addBusinessDays function
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

function IprFormRenderer({
  form,
  isPreview = false,
  shareId = null,
  additionalInfo,
  previousSubmission = null,
  submissionId = null,
  onGetFormResponseByPurchaseOrderId,
  mantisBusinessUserId,
}) {
  const [formValues, setFormValues] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [iprId, setIprId] = useState(null);
  const navigate = useNavigate();
  // Add effect to populate form with previous submission data
  useEffect(() => {
    if (previousSubmission) {
      // Set form values from previous submission
      setFormValues(previousSubmission.responses || {});

      // Set items if they exist in the previous submission
      if (previousSubmission.additionalInfo.items) {
        setItems(
          previousSubmission.additionalInfo.items.map((item) => ({
            ...item,
            id: item.id || `${item.value}-${Date.now()}`,
            label: item.label || item.name,
            value: item.value || item.sku,
          }))
        );
      }

      // Set products if they exist in the previous submission
      if (previousSubmission.additionalInfo.products) {
        setProducts(
          previousSubmission.additionalInfo.products.map((product) => ({
            ...product,
            id: product.id || `product-${Date.now()}`,
            name: product.name || product.label,
            items: product.items || [],
          }))
        );
      }

      if (previousSubmission.additionalInfo.clientPurchaseOrderId) {
        setClientPurchaseOrderId(
          previousSubmission.additionalInfo.clientPurchaseOrderId
        );
      }
    }
  }, [previousSubmission]);

  //IPR forms
  const [showCreateInventoryItemModal, setShowCreateInventoryItemModal] =
    useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientPurchaseOrderId, setClientPurchaseOrderId] = useState("");
  const [, setLoadingSubitems] = useState(false);
  const [showSubitemsModal, setShowSubitemsModal] = useState(false);
  const [subitems, setSubitems] = useState([]);
  const productionCategories = [
    "PRODUCCION",
    "LAMINAS",
    "PINTURA",
    "SERVICIOS DE PROVEEDORES",
    "ACCESORIOS",
    "TORNILLERÍA",
  ];
  const perfileriaCategories = ["PERFILERIA"];
  const maintenanceCategories = [
    "ALMACEN Y MANTENIMIENTO",
    "MANTENIMIENTO",
    "UTILES PAPELERÍA Y FOTOCOPIAS",
    "DOTACION Y EPPS",
    "BOTIQUIN",
    "MUEBLES Y ENSERES",
    "EQUIPO DE COMPUTO Y COMUNICACION",
    "SERVICIOS DE PROVEEDORES",
    "REPARACIÓN LOCATIVA",
  ];

  const { toast } = useToast();

  const handleInputChange = (fieldId, value) => {
    console.log(value);
    setFormValues({
      ...formValues,
      [fieldId]: value,
    });
  };

  const handleFetchSubitems = async (itemId) => {
    setLoadingSubitems(true);
    const res = await getSubitemsByItemId(itemId, form.mantisBusinessId);
    console.log("Subitems fetched:", res);
    if (res.success) {
      const subitems = res.data;
      setSubitems(subitems);
      setShowSubitemsModal(true);
    }
    setLoadingSubitems(false);
  };

  // Abrir modal para crear un nuevo item
  const handleCreateItem = () => {
    setShowCreateInventoryItemModal(true);
  };

  // Abrir modal para crear un nuevo cliente
  const handleCreateClient = () => {
    setShowCreateClientModal(true);
  };

  // Abrir modal para crear un nuevo producto
  const handleCreateProduct = () => {
    setShowCreateProductModal(true);
  };

  // Cerrar modal
  const handleCloseInventoryModal = (newItem) => {
    setShowCreateInventoryItemModal(false);
    if (newItem) {
      let item = {
        ...newItem,
        id: newItem.id || 0,
        name: newItem.name || "",
        label: newItem.name || "",
        value: newItem.sku || "",
      };
      setItems([item, ...items]);
      additionalInfo.inventoryItems.push(item);
    }
  };

  const handlePasteIPRItems = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("Text");
    const rows = pastedData
      .trim()
      .split("\n")
      .map((row) => row.split("\t"));
    if (rows.length > 0) {
      let newItems = [];
      //TODO search for existing items in inventory based on the name
      rows.forEach((row) => {
        if (row.length >= 3) {
          const [name, quantity, measurementUnit] = row;
          const existingItem = additionalInfo.inventoryItems.find(
            (item) => item.name.trim() === name.trim()
          );
          if (existingItem) {
            newItems.push({
              ...existingItem,
              quantity: parseFloat(quantity.trim()) || 0,
              measurementUnit: measurementUnit.trim(),
            });
          } else {
            newItems.push({
              name: name.trim(),
              label: name.trim(),
              value: name.trim().toLowerCase().replace(/\s+/g, "-"),
              quantity: parseFloat(quantity.trim()) || 0,
              measurementUnit: measurementUnit.trim(),
              mpClassification: "",
              deliveryDate: "",
              category_name: "PRODUCCION",
            });
          }
        } else if (row.length === 2) {
          const [name, quantity] = row;
          const existingItem = additionalInfo.inventoryItems.find(
            (item) => item.name.trim() === name.trim()
          );
          if (existingItem) {
            newItems.push({
              ...existingItem,
              quantity: parseFloat(quantity.trim()) || 0,
            });
          } else {
            newItems.push({
              name: name.trim(),
              label: name.trim(),
              value: name.trim().toLowerCase().replace(/\s+/g, "-"),
              quantity: parseFloat(quantity.trim()) || 0,
              measurementUnit: "",
              mpClassification: "",
              deliveryDate: "",
              category_name: "PRODUCCION",
            });
          }
        } else if (row.length === 1) {
          const name = row[0].trim();
          const existingItem = additionalInfo.inventoryItems.find(
            (item) => item.name.trim() === name.trim()
          );
          if (existingItem) {
            console.log("Existing item:", existingItem);
            newItems.push(existingItem);
          } else {
            console.log("New item:", name);
            newItems.push({
              name: name,
              label: name,
              value: name.toLowerCase().replace(/\s+/g, "-"),
              quantity: 0,
              measurementUnit: "",
              mpClassification: "",
              deliveryDate: "",
              category_name: "PRODUCCION",
            });
          }
        }
      });
      console.log("New items from paste:", newItems);
      setItems((prevItems) => [
        ...prevItems,
        ...newItems.map((item, index) => ({
          ...item,
          id: item.id || prevItems.length + index + 1, // Unique ID for temp items
        })),
      ]);
    }
    console.log("Pasted rows:", rows);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    const requiredFields = form.fields.filter((field) => field.required);
    const missingFields = requiredFields.filter((field) => {
      // Conditional logic check: if field is hidden, it's not "missing" for validation
      if (field.conditionalLogic?.enabled) {
        const dependsOnField = form.fields.find(
          (f) => f.id === field.conditionalLogic.dependsOn
        );
        if (dependsOnField) {
          const dependsOnValue = formValues[dependsOnField.id];
          const { showWhen, value: conditionalValue } = field.conditionalLogic;
          let shouldShow = false;
          switch (showWhen) {
            case "equals":
              shouldShow = dependsOnValue === conditionalValue;
              break;
            case "not_equals":
              shouldShow = dependsOnValue !== conditionalValue;
              break;
            case "contains":
              shouldShow = dependsOnValue?.includes(conditionalValue);
              break;
            case "not_contains":
              shouldShow = !dependsOnValue?.includes(conditionalValue);
              break;
            case "is_empty":
              shouldShow = !dependsOnValue || dependsOnValue === "";
              break;
            case "is_not_empty":
              shouldShow = dependsOnValue && dependsOnValue !== "";
              break;
            default:
              shouldShow = true;
          }
          if (!shouldShow) return false; // If not shown, not missing
        }
      }
      // Actual missing check
      if (field.type === "checkbox") {
        return !formValues[field.id]; // For required checkboxes, value must be true
      }
      return !formValues[field.id];
    });

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields
        .map((f) => f.label || f.id)
        .join(", ");
      toast({
        title: "Error en el formulario",
        description: `Por favor, completa todos los campos requeridos: ${missingFieldNames}.`,
        variant: "destructive",
        style: { borderRadius: "10px" },
      });
      setIsSubmitting(false);
      return;
    }

    if (isPreview) {
      toast({
        title: "Vista previa enviada",
        description: "Así se verían los datos del formulario.",
        style: {
          borderRadius: "12px",
          backgroundColor: "var(--doc-bg-secondary)",
        },
      });
      setFormSubmitted(true);
      setIsSubmitting(false);
      return;
    } else {
      try {
        const response = await submitPublicForm(
          shareId,
          formValues,
          items,
          submissionId, // Pass submissionId if it exists (for updates)
          clientPurchaseOrderId,
          products, // Pass products data
          mantisBusinessUserId
        );
        if (response.success) {
          setIprId(response.data.initialPurchaseRequest.id);
        }

        toast({
          title: submissionId
            ? "¡Formulario actualizado con éxito! 🎉"
            : "¡Formulario enviado con éxito! 🎉",
          description: (
            <div className="mt-2 space-y-2">
              <p>
                {submissionId
                  ? "Gracias por actualizar el formulario."
                  : "Gracias por completar el formulario."}
              </p>
              <p className="text-sm text-gray-500">
                Tu información ha sido{" "}
                {submissionId ? "actualizada" : "recibida"} correctamente.
              </p>
            </div>
          ),
          style: {
            borderRadius: "12px",
            backgroundColor: "white",
            border: "1px solid var(--color-secondary)",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          },
        });

        setFormSubmitted(true);
      } catch (error) {
        toast({
          title: "Error al enviar",
          description: (
            <div className="mt-2 space-y-2">
              <p>
                Hubo un problema al {submissionId ? "actualizar" : "enviar"} tu
                formulario.
              </p>
              <p className="text-sm text-gray-500">
                Por favor, inténtalo de nuevo.
              </p>
            </div>
          ),
          variant: "destructive",
          style: {
            borderRadius: "12px",
            backgroundColor: "white",
            border: "1px solid #ef4444",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Actualizar la función renderField para incluir el tipo "file"
  const renderField = (field) => {
    // Verificar si el campo debe mostrarse según la lógica condicional
    if (field.conditionalLogic?.enabled) {
      const dependsOnField = form.fields.find(
        (f) => f.id === field.conditionalLogic.dependsOn
      );
      if (!dependsOnField) return null;

      const dependsOnValue = formValues[dependsOnField.id];
      const { showWhen, value } = field.conditionalLogic;

      // Evaluar la condición
      let shouldShow = false;

      switch (showWhen) {
        case "equals":
          shouldShow = dependsOnValue === value;
          break;
        case "not_equals":
          shouldShow = dependsOnValue !== value;
          break;
        case "contains":
          shouldShow = dependsOnValue?.includes(value);
          break;
        case "not_contains":
          shouldShow = !dependsOnValue?.includes(value);
          break;
        case "is_empty":
          shouldShow = !dependsOnValue || dependsOnValue === "";
          break;
        case "is_not_empty":
          shouldShow = dependsOnValue && dependsOnValue !== "";
          break;
        default:
          shouldShow = true;
      }

      if (!shouldShow) return null;
    }
    //Separate Acermetalicas items by category
    let produccionItems = items.filter((item) =>
      productionCategories.includes(item.category_name)
    );

    let perfileriaItems = items.filter((item) =>
      perfileriaCategories.includes(item.category_name)
    );

    let almacenItems = items.filter((item) =>
      maintenanceCategories.includes(item.category_name)
    );

    // Items que no pertenecen a ninguna categoría específica
    let defaultItems = items.filter(
      (item) =>
        !productionCategories.includes(item.category_name) &&
        !perfileriaCategories.includes(item.category_name) &&
        !maintenanceCategories.includes(item.category_name)
    );
    switch (field.type) {
      case "text":
        if (field.iprFieldType === "client_purchase_order_id") {
          return (
            <div className="space-y-4 relative z-[9999]">
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Input
                    id={field.id}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={formValues[field.id] || clientPurchaseOrderId}
                    onChange={(e) => {
                      setClientPurchaseOrderId(e.target.value);
                      handleInputChange(field.id, e.target.value);
                    }}
                    required={field.required}
                    className="w-full h-12 bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    style={{ borderRadius: "12px" }}
                    disabled={formSubmitted}
                  />
                </div>
                <Button
                  type="button"
                  className="px-4 py-2 h-[40px] whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200"
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={() => {
                    onGetFormResponseByPurchaseOrderId(clientPurchaseOrderId);
                  }}
                >
                  Obtener Info
                </Button>
              </div>
            </div>
          );
        } else {
          return (
            <Input
              id={field.id}
              type={field.type}
              placeholder={field.placeholder}
              value={formValues[field.id] || ""}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              required={field.required}
              className="w-full h-12 bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              style={{ borderRadius: "12px" }}
              disabled={formSubmitted}
            />
          );
        }
      case "email":
      case "number":
      case "tel":
        return (
          <Input
            id={field.id}
            type={field.type}
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            className="w-full h-12 bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            style={{ borderRadius: "12px" }}
            disabled={formSubmitted}
          />
        );

      case "textarea":
        return (
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            className="w-full min-h-[120px] bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            style={{ borderRadius: "12px" }}
            disabled={formSubmitted}
          />
        );

      case "select":
        return (
          <Select
            value={
              field.multiple
                ? field.options.filter((option) =>
                    (formValues[field.id] || []).includes(option.value)
                  )
                : field.options.find(
                    (option) => option.value === formValues[field.id]
                  ) || null
            }
            onChange={(selectedOption) => {
              if (field.multiple) {
                handleInputChange(
                  field.id,
                  selectedOption ? selectedOption.map((opt) => opt.value) : []
                );
              } else {
                handleInputChange(field.id, selectedOption?.value || "");
              }
            }}
            options={field.options}
            isDisabled={formSubmitted}
            isRequired={field.required}
            isMulti={!!field.multiple}
            placeholder={field.placeholder || "Seleccionar opción"}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "white",
                borderRadius: "12px",
                borderColor: "var(--color-secondary)",
                minHeight: "48px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "&:hover": {
                  borderColor: "var(--color-secondary)",
                },
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                zIndex: 9999,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused
                  ? "var(--color-secondary)"
                  : "transparent",
                color: state.isFocused ? "white" : "var(--doc-text-primary)",
                padding: "12px 16px",
                "&:active": {
                  backgroundColor: "var(--color-secondary)",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color: "var(--doc-text-primary)",
                fontSize: "0.95rem",
              }),
              placeholder: (base) => ({
                ...base,
                color: "var(--doc-text-muted)",
                fontSize: "0.95rem",
              }),
            }}
            isVirtualized={true}
            isSearchable={true}
            menuPortalTarget={
              typeof window !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
          />
        );

      case "radio":
        return (
          <RadioGroup
            value={formValues[field.id] || ""}
            onValueChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
            disabled={formSubmitted}
            className="space-y-3"
          >
            {field.options.map((option) => (
              <div
                key={option.id}
                className="flex items-center space-x-3 bg-white p-4 rounded border border-gray-200 shadow-sm hover:border-orange-500 transition-all duration-200"
                style={{ borderRadius: "12px" }}
              >
                <RadioGroupItem
                  value={option.value}
                  id={`${field.id}-${option.id}`}
                  className="h-5 w-5"
                  style={{
                    borderColor: "var(--color-secondary)",
                    "&:checked": { backgroundColor: "var(--color-secondary)" },
                  }}
                />
                <Label
                  htmlFor={`${field.id}-${option.id}`}
                  className="flex-grow cursor-pointer text-gray-700"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "checkbox":
        return (
          <div
            className="flex items-center space-x-3 bg-white p-4 rounded border border-gray-200 shadow-sm hover:border-orange-500 transition-all duration-200"
            style={{ borderRadius: "12px" }}
          >
            <Checkbox
              id={field.id}
              checked={formValues[field.id] || false}
              onCheckedChange={(checked) =>
                handleInputChange(field.id, checked)
              }
              required={field.required}
              disabled={formSubmitted}
              className="h-5 w-5"
              style={{
                borderColor: "var(--color-secondary)",
                "&:checked": { backgroundColor: "var(--color-secondary)" },
              }}
            />
            <Label htmlFor={field.id} className="cursor-pointer text-gray-700">
              {field.placeholder || "Acepto"}
            </Label>
          </div>
        );

      // Añadir el caso para "file"
      case "file":
        return (
          <FileUploadField
            id={field.id}
            label={field.label}
            required={field.required}
            multiple={field.multiple}
            accept={field.accept || ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            value={formValues[field.id] || []}
            onChange={(files) => {
              handleInputChange(field.id, files);
            }}
            disabled={formSubmitted}
            style={{ borderRadius: "8px" }}
          />
        );

      case "date":
        return (
          <Input
            id={field.id}
            type="date"
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            min={field.minDate}
            max={field.maxDate}
            className="bg-white"
            style={{ borderRadius: "8px" }}
            disabled={formSubmitted}
          />
        );

      case "time":
        return (
          <Input
            id={field.id}
            type="time"
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            min={field.minTime}
            max={field.maxTime}
            className="bg-white"
            style={{ borderRadius: "8px" }}
            disabled={formSubmitted}
          />
        );

      case "external_select":
        if (field.externalSelectType === "inventory_items_maintenance") {
          return (
            <div className="space-y-4 relative z-[9999]">
              <Textarea
                onPaste={handlePasteIPRItems}
                placeholder="Copia y pega los items aquí"
              />
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Select
                    value={
                      items.map((item) => ({
                        value: item.value,
                        label: item.label,
                      })) || []
                    }
                    onChange={(selectedOptions) => {
                      handleInputChange(
                        field.id,
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      );
                      setItems((prevItems) => {
                        const selectedValues = selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : [];
                        const updatedItems = prevItems.filter((item) =>
                          selectedValues.includes(item.value)
                        );
                        const newItems = (selectedOptions || [])
                          .filter(
                            (option) =>
                              !updatedItems.some(
                                (item) => item.value === option.value
                              )
                          )
                          .map((option) => ({
                            ...option,
                            quantity: 0,
                            mpClassification: "",
                            measurementUnit: "",
                            oc: "",
                          }));
                        return [...updatedItems, ...newItems];
                      });
                    }}
                    options={additionalInfo?.inventoryItems || []}
                    isDisabled={formSubmitted}
                    isRequired={field.required}
                    placeholder={field.placeholder || "Seleccionar opciones"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        borderColor: "var(--color-secondary)",
                        minHeight: "40px",
                        "&:hover": {
                          borderColor: "var(--color-secondary)",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "var(--doc-bg-secondary)",
                        color: "var(--doc-text-primary)",
                        borderRadius: "8px",
                        zIndex: 9999,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "var(--color-secondary)"
                          : "transparent",
                        color: state.isFocused
                          ? "white"
                          : "var(--doc-text-primary)",
                        "&:active": {
                          backgroundColor: "var(--color-secondary)",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "var(--color-secondary)",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        padding: "2px 6px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "var(--color-secondary-dark)",
                          color: "white",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--doc-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--doc-text-muted)",
                      }),
                    }}
                    isVirtualized={true}
                    isSearchable={true}
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isLoading={!additionalInfo?.inventoryItems}
                    filterOption={(option, inputValue) => {
                      const label = option.label.toLowerCase();
                      const sku = option.value;
                      const search = inputValue.toLowerCase();
                      return label.includes(search) || sku?.includes(search);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  className="px-4 py-2 h-[40px] whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200"
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={handleCreateItem}
                >
                  Crear Item
                </Button>
              </div>

              {/*Almacen Items Table */}
              {almacenItems.length > 0 && (
                <div className="mt-4 border rounded">
                  <div className="px-4 py-2 bg-muted/30 border-b">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                      Almacén y Mantenimiento
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Item
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Cantidad
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Fecha de Entrega
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {almacenItems.map((item, index) => (
                          <tr key={item.id} className="bg-white">
                            <td className="px-4 py-2">{item.label}</td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          quantity:
                                            parseFloat(e.target.value) || 0,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                className="w-24"
                              />
                            </td>

                            <td className="px-4 py-2">
                              <Input
                                type="date"
                                value={item.deliveryDate}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? { ...i, deliveryDate: e.target.value }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                placeholder="Fecha de Entrega"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  id={`file-input-${item.id}`}
                                  className="hidden"
                                  multiple
                                  onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    const filesWithBase64 = await Promise.all(
                                      files.map(async (file) => ({
                                        filename: file.name,
                                        size_in_bytes: file.size,
                                        mime_type: file.type,
                                        base64: await fileToBase64(file),
                                      }))
                                    );

                                    const newItems = items.map((i) =>
                                      i.id === item.id
                                        ? {
                                            ...i,
                                            attachments: filesWithBase64,
                                          }
                                        : i
                                    );
                                    setItems(newItems);
                                  }}
                                />
                                <Button
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(`file-input-${item.id}`)
                                      .click()
                                  }
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                </Button>
                                {item.attachments?.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({item.attachments.length})
                                  </span>
                                )}
                                <Button
                                  type="button"
                                  onClick={() => {
                                    handleFetchSubitems(item.id);
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </Button>
                              </div>
                            </td>
                            <td className="text-center w-[30px]">
                              <button
                                type="button"
                                onClick={() => {
                                  setItems(
                                    items.filter((i) => i.id !== item.id)
                                  );
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        }
        if (field.externalSelectType === "inventory_items_production") {
          return (
            <div className="space-y-4 relative z-[9999]">
              <Textarea
                onPaste={handlePasteIPRItems}
                placeholder="Copia y pega los items aquí"
              />
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Select
                    value={
                      items.map((item) => ({
                        value: item.value,
                        label: item.label,
                      })) || []
                    }
                    onChange={(selectedOptions) => {
                      handleInputChange(
                        field.id,
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      );
                      setItems((prevItems) => {
                        const selectedValues = selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : [];
                        const updatedItems = prevItems.filter((item) =>
                          selectedValues.includes(item.value)
                        );
                        const newItems = (selectedOptions || [])
                          .filter(
                            (option) =>
                              !updatedItems.some(
                                (item) => item.value === option.value
                              )
                          )
                          .map((option) => ({
                            ...option,
                            quantity: 0,
                            mpClassification: "",
                            measurementUnit: "",
                            oc: "",
                          }));
                        return [...updatedItems, ...newItems];
                      });
                    }}
                    options={additionalInfo?.inventoryItems || []}
                    isDisabled={formSubmitted}
                    isRequired={field.required}
                    placeholder={field.placeholder || "Seleccionar opciones"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        borderColor: "var(--color-secondary)",
                        minHeight: "40px",
                        "&:hover": {
                          borderColor: "var(--color-secondary)",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "var(--doc-bg-secondary)",
                        color: "var(--doc-text-primary)",
                        borderRadius: "8px",
                        zIndex: 9999,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "var(--color-secondary)"
                          : "transparent",
                        color: state.isFocused
                          ? "white"
                          : "var(--doc-text-primary)",
                        "&:active": {
                          backgroundColor: "var(--color-secondary)",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "var(--color-secondary)",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        padding: "2px 6px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "var(--color-secondary-dark)",
                          color: "white",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--doc-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--doc-text-muted)",
                      }),
                    }}
                    isVirtualized={true}
                    isSearchable={true}
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isLoading={!additionalInfo?.inventoryItems}
                    filterOption={(option, inputValue) => {
                      const label = option.label.toLowerCase();
                      const sku = option.value;
                      const search = inputValue.toLowerCase();
                      return label.includes(search) || sku?.includes(search);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  className="px-4 py-2 h-[40px] whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200"
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={handleCreateItem}
                >
                  Crear Item
                </Button>
              </div>

              {/*Produccion Items Table */}
              {produccionItems.length > 0 && (
                <div className="mt-4 border rounded">
                  <div className="px-4 py-2 bg-muted/30 border-b">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                      Producción
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Item
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Cantidad
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Unidad de Medida
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Clasificación MP
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Fecha de Entrega
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {produccionItems.map((item, index) => (
                          <tr key={item.id} className="bg-white">
                            <td className="px-4 py-2">{item.label}</td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          quantity:
                                            parseFloat(e.target.value) || 0,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                className="w-24"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Select
                                value={{
                                  value: item.measurementUnit,
                                  label: item.measurementUnit,
                                }}
                                onChange={(selected) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          measurementUnit: selected.value,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                options={[
                                  { value: "UND", label: "UND" },
                                  { value: "KL", label: "KL" },
                                  { value: "MTS", label: "MTS" },
                                ]}
                                className="w-24"
                                classNamePrefix="react-select"
                                menuPortalTarget={
                                  typeof window !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    backgroundColor: "white",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Select
                                value={{
                                  value: item.mpClassification,
                                  label: item.mpClassification,
                                }}
                                onChange={(selected) => {
                                  const today = new Date();
                                  let deliveryDate = new Date(today);
                                  if (selected.value === "TERMINADOS") {
                                    // Add 4 business days to the date
                                    deliveryDate = addBusinessDays(
                                      deliveryDate,
                                      4
                                    );
                                  } else if (selected.value === "SOLDADURA") {
                                    // Add 2 business days to the date
                                    deliveryDate = addBusinessDays(
                                      deliveryDate,
                                      2
                                    );
                                  }
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          mpClassification: selected.value,
                                          deliveryDate: deliveryDate
                                            .toISOString()
                                            .split("T")[0],
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                options={[
                                  { value: "SOLDADURA", label: "SOLDADURA" },
                                  {
                                    value: "TERMINADOS",
                                    label: "TERMINADOS",
                                  },
                                ]}
                                className="w-40"
                                classNamePrefix="react-select"
                                menuPortalTarget={
                                  typeof window !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    backgroundColor: "white",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            </td>

                            <td className="px-4 py-2">
                              <Input
                                type="date"
                                value={item.deliveryDate}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? { ...i, deliveryDate: e.target.value }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                placeholder="Fecha de Entrega"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  id={`file-input-${item.id}`}
                                  className="hidden"
                                  multiple
                                  onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    const filesWithBase64 = await Promise.all(
                                      files.map(async (file) => ({
                                        filename: file.name,
                                        size_in_bytes: file.size,
                                        mime_type: file.type,
                                        base64: await fileToBase64(file),
                                      }))
                                    );

                                    const newItems = items.map((i) =>
                                      i.id === item.id
                                        ? {
                                            ...i,
                                            attachments: filesWithBase64,
                                          }
                                        : i
                                    );
                                    setItems(newItems);
                                  }}
                                />
                                <Button
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(`file-input-${item.id}`)
                                      .click()
                                  }
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                </Button>
                                {item.attachments?.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({item.attachments.length})
                                  </span>
                                )}
                                <Button
                                  type="button"
                                  onClick={() => {
                                    handleFetchSubitems(item.id);
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </Button>
                              </div>
                            </td>
                            <td className="text-center w-[30px]">
                              <button
                                type="button"
                                onClick={() => {
                                  setItems(
                                    items.filter((i) => i.id !== item.id)
                                  );
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/*Perfileria Items Table */}
              {perfileriaItems.length > 0 && (
                <div className="mt-4 border rounded">
                  <div className="px-4 py-2 bg-muted/30 border-b">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                      Perfilería
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Item
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Cantidad
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Longitud
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Ángulos/Archivo
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Novedad
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Fecha de Entrega
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {perfileriaItems.map((item, index) => (
                          <tr key={item.id} className="bg-white">
                            <td className="px-4 py-2">{item.label}</td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          quantity:
                                            parseFloat(e.target.value) || 0,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                className="w-24"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.longitude}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          longitude:
                                            parseFloat(e.target.value) || 0,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                className="w-24"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="text"
                                value={item.angles}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? { ...i, angles: e.target.value }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                placeholder="Ángulos/Archivo"
                                className="bg-white"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Select
                                value={{
                                  value: item.novelty,
                                  label: item.novelty,
                                }}
                                onChange={(selected) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? { ...i, novelty: selected.value }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                options={[
                                  { value: "SOLDADURA", label: "SOLDADURA" },
                                  {
                                    value: "MECANIZADO",
                                    label: "MECANIZADO",
                                  },
                                  { value: "DOBLEZ", label: "DOBLEZ" },
                                  { value: "LASER", label: "LASER" },
                                  {
                                    value: "SERVICIOS EXTERNOS",
                                    label: "SERVICIOS EXTERNOS",
                                  },
                                ]}
                                className="w-40"
                                classNamePrefix="react-select"
                                menuPortalTarget={
                                  typeof window !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    backgroundColor: "white",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            </td>

                            <td className="px-4 py-2">
                              <Input
                                type="date"
                                value={item.deliveryDate}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? { ...i, deliveryDate: e.target.value }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                placeholder="Fecha de Entrega"
                              />
                            </td>

                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  id={`file-input-${item.id}`}
                                  className="hidden"
                                  multiple
                                  onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    const filesWithBase64 = await Promise.all(
                                      files.map(async (file) => ({
                                        filename: file.name,
                                        size_in_bytes: file.size,
                                        mime_type: file.type,
                                        base64: await fileToBase64(file),
                                      }))
                                    );

                                    const newItems = items.map((i) =>
                                      i.id === item.id
                                        ? {
                                            ...i,
                                            attachments: filesWithBase64,
                                          }
                                        : i
                                    );
                                    setItems(newItems);
                                  }}
                                />
                                <Button
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(`file-input-${item.id}`)
                                      .click()
                                  }
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                </Button>
                                {item.attachments?.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({item.attachments.length})
                                  </span>
                                )}
                                <Button
                                  type="button"
                                  onClick={() => {
                                    handleFetchSubitems(item.id);
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
                                  style={{
                                    borderRadius: "6px",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </Button>
                              </div>
                            </td>
                            <td className="text-center w-[30px]">
                              <button
                                type="button"
                                onClick={() => {
                                  setItems(
                                    items.filter((i) => i.id !== item.id)
                                  );
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/*Default Items Table - Para items sin categoría específica */}
              {defaultItems.length > 0 && (
                <div className="mt-6 border border-gray-200 rounded shadow-sm bg-white">
                  <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 rounded-t-lg">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Items Seleccionados
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({defaultItems.length})
                      </span>
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Cantidad
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Unidad de Medida
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {defaultItems.map((item, index) => (
                          <tr
                            key={item.id}
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <svg
                                    className="w-4 h-4 text-blue-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {item.label}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    SKU: {item.value || item.sku || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity || 0}
                                onChange={(e) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          quantity:
                                            parseFloat(e.target.value) || 0,
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                className="w-20 text-center border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Select
                                value={
                                  item.measurementUnit
                                    ? {
                                        value: item.measurementUnit,
                                        label: item.measurementUnit,
                                      }
                                    : null
                                }
                                onChange={(selected) => {
                                  const newItems = items.map((i) =>
                                    i.id === item.id
                                      ? {
                                          ...i,
                                          measurementUnit:
                                            selected?.value || "",
                                        }
                                      : i
                                  );
                                  setItems(newItems);
                                }}
                                options={[
                                  { value: "UND", label: "UND" },
                                  { value: "KG", label: "KG" },
                                  { value: "MTS", label: "MTS" },
                                  { value: "LT", label: "LT" },
                                  { value: "M2", label: "M2" },
                                  { value: "M3", label: "M3" },
                                  { value: "CAJA", label: "CAJA" },
                                  { value: "PAQUETE", label: "PAQUETE" },
                                ]}
                                placeholder="Seleccionar unidad"
                                className="w-44"
                                classNamePrefix="react-select"
                                menuPortalTarget={
                                  typeof window !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "38px",
                                    backgroundColor: "white",
                                    borderColor: "#d1d5db",
                                    borderRadius: "6px",
                                    boxShadow:
                                      "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                    "&:hover": {
                                      borderColor: "#3b82f6",
                                    },
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    backgroundColor: "white",
                                    borderRadius: "6px",
                                    boxShadow:
                                      "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                  }),
                                  option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused
                                      ? "#dbeafe"
                                      : "transparent",
                                    color: "#374151",
                                    "&:active": {
                                      backgroundColor: "#bfdbfe",
                                    },
                                  }),
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  id={`file-input-${item.id}`}
                                  className="hidden"
                                  multiple
                                  onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    const filesWithBase64 = await Promise.all(
                                      files.map(async (file) => ({
                                        filename: file.name,
                                        size_in_bytes: file.size,
                                        mime_type: file.type,
                                        base64: await fileToBase64(file),
                                      }))
                                    );

                                    const newItems = items.map((i) =>
                                      i.id === item.id
                                        ? {
                                            ...i,
                                            attachments: filesWithBase64,
                                          }
                                        : i
                                    );
                                    setItems(newItems);
                                  }}
                                />
                                <Button
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(`file-input-${item.id}`)
                                      .click()
                                  }
                                  className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200 rounded shadow-sm"
                                  style={{
                                    border: "none",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                  Adjuntar
                                </Button>
                                {item.attachments?.length > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {item.attachments.length} archivo
                                    {item.attachments.length !== 1 ? "s" : ""}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setItems(
                                      items.filter((i) => i.id !== item.id)
                                    );
                                  }}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-200"
                                  title="Eliminar item"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        }
        if (field.externalSelectType === "clients") {
          return (
            <div className="space-y-4 relative z-[9999]">
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Select
                    value={
                      additionalInfo?.clients?.find(
                        (option) => option.label === formValues[field.id]
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      handleInputChange(field.id, selectedOption.label);
                    }}
                    options={additionalInfo?.clients || []}
                    isDisabled={formSubmitted}
                    isRequired={field.required}
                    placeholder={field.placeholder || "Seleccionar opciones"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={false}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        borderColor: "var(--color-secondary)",
                        minHeight: "40px",
                        "&:hover": {
                          borderColor: "var(--color-secondary)",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "var(--doc-bg-secondary)",
                        color: "var(--doc-text-primary)",
                        borderRadius: "8px",
                        zIndex: 9999,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "var(--color-secondary)"
                          : "transparent",
                        color: state.isFocused
                          ? "white"
                          : "var(--doc-text-primary)",
                        "&:active": {
                          backgroundColor: "var(--color-secondary)",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "var(--color-secondary)",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        padding: "2px 6px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "var(--color-secondary-dark)",
                          color: "white",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--doc-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--doc-text-muted)",
                      }),
                    }}
                    isVirtualized={true}
                    isSearchable={true}
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isLoading={!additionalInfo?.clients}
                    filterOption={(option, inputValue) => {
                      const label = option.label.toLowerCase();
                      const search = inputValue.toLowerCase();
                      return label.includes(search);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  className="px-4 py-2 h-[40px] whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200"
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={handleCreateClient}
                >
                  Crear Cliente
                </Button>
              </div>
            </div>
          );
        }
        if (field.externalSelectType === "costCenters") {
          return (
            <div className="space-y-4 relative z-[9999]">
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Select
                    value={
                      additionalInfo?.costCenters?.find(
                        (option) => option.label === formValues[field.id]
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      handleInputChange(field.id, selectedOption.label);
                    }}
                    options={additionalInfo?.costCenters || []}
                    isDisabled={formSubmitted}
                    isRequired={field.required}
                    placeholder={field.placeholder || "Seleccionar opciones"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={false}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        borderColor: "var(--color-secondary)",
                        minHeight: "40px",
                        "&:hover": {
                          borderColor: "var(--color-secondary)",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "var(--doc-bg-secondary)",
                        color: "var(--doc-text-primary)",
                        borderRadius: "8px",
                        zIndex: 9999,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "var(--color-secondary)"
                          : "transparent",
                        color: state.isFocused
                          ? "white"
                          : "var(--doc-text-primary)",
                        "&:active": {
                          backgroundColor: "var(--color-secondary)",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "var(--color-secondary)",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        padding: "2px 6px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "var(--color-secondary-dark)",
                          color: "white",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--doc-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--doc-text-muted)",
                      }),
                    }}
                    isVirtualized={true}
                    isSearchable={true}
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isLoading={!additionalInfo?.costCenters}
                    filterOption={(option, inputValue) => {
                      const label = option.label.toLowerCase();
                      const search = inputValue.toLowerCase();
                      return label.includes(search);
                    }}
                  />
                </div>
              </div>
            </div>
          );
        }
        if (field.externalSelectType === "product") {
          return (
            <div className="space-y-6 relative z-[9999]">
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Select
                    value={products.map((product) => ({
                      label: product.name,
                      value: product.id,
                    }))}
                    onChange={(selectedOptions) => {
                      const selectedProductsArray = selectedOptions || [];

                      // Update form values with product names
                      const productNames = selectedProductsArray.map(
                        (opt) => opt.label
                      );
                      handleInputChange(field.id, productNames);

                      // Update products state - only existing products
                      const newProducts = selectedProductsArray.map(
                        (option) => {
                          // For existing products from additionalInfo
                          const existingProduct = additionalInfo.products.find(
                            (product) => product.id === option.value
                          );
                          return (
                            existingProduct || {
                              id: option.value,
                              name: option.label,
                              items: [],
                            }
                          );
                        }
                      );

                      setProducts(newProducts);
                    }}
                    options={additionalInfo?.products || []}
                    isDisabled={formSubmitted}
                    isRequired={field.required}
                    placeholder={field.placeholder || "Seleccionar productos"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={true}
                    isClearable={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        borderColor: "var(--color-secondary)",
                        minHeight: "40px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        "&:hover": {
                          borderColor: "var(--color-secondary)",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "white",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        zIndex: 9999,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "var(--color-secondary)"
                          : "transparent",
                        color: state.isFocused
                          ? "white"
                          : "var(--doc-text-primary)",
                        padding: "12px 16px",
                        "&:active": {
                          backgroundColor: "var(--color-secondary)",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "var(--color-secondary)",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        padding: "2px 6px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "var(--color-secondary-dark)",
                          color: "white",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--doc-text-primary)",
                        fontSize: "0.95rem",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--doc-text-muted)",
                        fontSize: "0.95rem",
                      }),
                    }}
                    isVirtualized={true}
                    isSearchable={true}
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isLoading={!additionalInfo?.products}
                    filterOption={(option, inputValue) => {
                      const label = option.label.toLowerCase();
                      const search = inputValue.toLowerCase();
                      const sku = option.value;
                      return label.includes(search) || sku?.includes(search);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  className="px-4 py-2 h-[40px] whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200"
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={handleCreateProduct}
                >
                  Crear Producto
                </Button>
              </div>

              {/* Products Display Section */}
              {products.length > 0 && (
                <div className="space-y-4">
                  <div className="border-b border-gray-200 pb-2">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                      Productos Seleccionados
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({products.length})
                      </span>
                    </h3>
                  </div>

                  {/* Products Grid Layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    {products.map((product, productIndex) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        productIndex={productIndex}
                        products={products}
                        setProducts={setProducts}
                        field={field}
                        handleInputChange={handleInputChange}
                        formSubmitted={formSubmitted}
                        availableItems={additionalInfo?.inventoryItems || []}
                        handleFetchSubitems={handleFetchSubitems}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }
        return <p>Tipo de campo no soportado: {field.type}</p>;

      default:
        return <p>Tipo de campo no soportado: {field.type}</p>;
    }
  };

  if (formSubmitted && !isPreview && iprId) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] animate-fadeIn">
        <Card
          className="shadow-2xl transform transition-all duration-500 animate-scaleIn relative z-[10000]"
          style={{
            borderTop: "4px solid var(--color-secondary)",
            borderRadius: "24px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "90%",
          }}
        >
          <CardHeader
            className="bg-gradient-to-r from-gray-50 to-white pb-6"
            style={{ borderRadius: "24px 24px 0 0" }}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-4 animate-bounce"
                style={{
                  backgroundColor: "rgba(var(--color-secondary-rgb), 0.1)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10"
                  style={{ color: "var(--color-secondary)" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <CardTitle
                className="text-3xl font-bold"
                style={{ color: "var(--color-secondary)" }}
              >
                ¡Formulario enviado!
              </CardTitle>
              <CardDescription className="text-lg text-gray-600">
                Gracias por completar el formulario.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-gray-600 text-lg">
              Tu información ha sido recibida correctamente.
            </p>
            <div className="flex justify-center space-x-4 mt-6">
              <Button
                onClick={() => {
                  setFormValues({});
                  setItems([]);
                  setFormSubmitted(false);
                }}
                className="px-6 py-3 text-base font-semibold transition-all duration-200 hover:scale-105 bg-orange-500 hover:bg-orange-600"
                style={{
                  color: "white",
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                Enviar otro formulario
              </Button>
              <Button
                onClick={() => {
                  navigate(`/business/backoffice/control-panel-p2o/${iprId}`);
                }}
                className="px-6 py-3 text-base font-semibold transition-all duration-200 hover:scale-105 bg-gray-500 hover:bg-gray-600"
                style={{
                  color: "white",
                  borderRadius: "12px",
                  border: "none",
                  backgroundColor: "var(--color-secondary)",
                }}
              >
                Ir a Control Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card
      className="shadow-lg hover:shadow-xl transition-all duration-300 mx-auto"
      style={{
        borderTop: "4px solid var(--color-secondary)",
        borderRadius: "16px",
        width: "100%",
        minWidth: "1500px",
        maxWidth: "1800px",
        backgroundColor: "white",
      }}
    >
      <CardHeader
        className="bg-gradient-to-r from-gray-50 to-white pb-6"
        style={{ borderRadius: "16px 16px 0 0" }}
      >
        <CardTitle
          className="text-2xl font-bold mb-2"
          style={{ color: "var(--color-secondary)" }}
        >
          {form.title}
          {submissionId && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              (Editando presentación anterior)
            </span>
          )}
        </CardTitle>
        {form.description && (
          <CardDescription className="text-gray-600 text-base">
            {form.description}
          </CardDescription>
        )}
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <div className="min-h-[600px] relative">
          <CardContent className="p-8">
            <div className="space-y-8">
              {form.fields.map((field, index) => {
                const fieldContent = renderField(field);
                if (!fieldContent) return null;

                const isDependedOn = form.fields.some(
                  (f) =>
                    f.conditionalLogic?.enabled &&
                    f.conditionalLogic.dependsOn === field.id
                );

                return (
                  <div
                    key={field.id}
                    className="space-y-4 group animate-fadeIn"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <div className="flex flex-col space-y-2">
                      <Label
                        htmlFor={field.id}
                        className="text-sm font-semibold text-gray-700 flex items-center group-hover:text-gray-900 transition-colors duration-200"
                      >
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      {field.helpText && (
                        <p className="text-sm text-gray-500 italic animate-fadeIn">
                          {field.helpText}
                        </p>
                      )}
                    </div>
                    <div className="transform transition-all duration-200 group-hover:translate-x-1">
                      {fieldContent}
                    </div>
                    {index < form.fields.length - 1 && (
                      <Separator className="mt-8 bg-gray-100" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </div>
        <CardFooter
          className="bg-gradient-to-r from-gray-50 to-white p-8 flex justify-end sticky bottom-0"
          style={{ borderRadius: "0 0 16px 16px" }}
        >
          <Button
            className="w-full md:w-auto px-8 py-6 text-base font-semibold transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed bg-orange-500 hover:bg-orange-600"
            disabled={isPreview ? false : isSubmitting || formSubmitted}
            style={{
              color: "white",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{submissionId ? "Actualizando..." : "Enviando..."}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>{submissionId ? "Actualizar" : "Enviar"}</span>
              </div>
            )}
          </Button>
        </CardFooter>
      </form>
      {showCreateInventoryItemModal && (
        <InventoryModal
          item={null}
          mode={"create"}
          onSave={(newItem) => handleCloseInventoryModal(newItem)}
          clientId={form.mantisBusinessId}
          categories={additionalInfo?.categories || []}
          onClose={() => setShowCreateInventoryItemModal(false)}
        />
      )}
      {showCreateClientModal && (
        <ClientFormModal
          isOpen={showCreateClientModal}
          onClose={() => setShowCreateClientModal(false)}
          onSuccess={(newClientData) => {
            setShowCreateClientModal(false);
            if (newClientData) {
              additionalInfo.clients.push({
                ...newClientData,
                value: newClientData.id,
                label: newClientData.name,
              });
            }
          }}
          mode="create"
          title="Crear Nuevo Cliente"
          clientId={form.mantisBusinessId}
        />
      )}
      {showCreateProductModal && (
        <ProductFormModal
          isOpen={showCreateProductModal}
          onClose={() => setShowCreateProductModal(false)}
          onSuccess={(newProductData) => {
            setShowCreateProductModal(false);
            if (newProductData.success) {
              additionalInfo.products.push({
                ...newProductData.data,
                value: newProductData.data.id,
                label: newProductData.data.name,
              });
            }
          }}
          mode="create"
          title="Crear Nuevo Producto"
          clientId={form.mantisBusinessId}
          availableItems={additionalInfo?.inventoryItems || []}
          categories={additionalInfo?.categories || []}
        />
      )}
      {showSubitemsModal && (
        <SubitemsModal
          subitems={subitems}
          onClose={() => setShowSubitemsModal(false)}
        />
      )}
    </Card>
  );
}

// Update the styles

export default connect(selectAuthState)(IprFormRenderer);